/**
 * Form Factor Selector Component
 * Generic toggle for selecting device form factor
 */

import React from 'react';
import { Label } from '@/core/components/ui/label';
import { Button } from '@/core/components/ui/button';
import { FormFactor } from '../../types/device-config-types';

interface FormFactorOption {
  value: FormFactor;
  label: string;
  dimensions: string;
}

interface FormFactorSelectorProps {
  /** Available form factors */
  options: FormFactorOption[];

  /** Currently selected form factor */
  value: FormFactor;

  /** Callback when form factor changes */
  onChange: (formFactor: FormFactor) => void;

  /** Optional label */
  label?: string;

  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * Form Factor Selector Component
 * Toggle between different device form factors
 */
export function FormFactorSelector({
  options,
  value,
  onChange,
  label = 'Form Factor',
  disabled = false,
}: FormFactorSelectorProps) {
  return (
    <div className="flex items-center gap-4">
      <Label className="text-sm font-medium whitespace-nowrap">{label}</Label>

      <div className="flex items-center gap-2 flex-1">
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <Button
              key={option.value}
              type="button"
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              className="flex-1"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-xs opacity-70">{option.dimensions}</span>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
